import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SubscriptionContext = createContext(null);
const DEFAULT_GAME_OPTIONS = [["", "All"], ["pokemon", "Pokémon"], ["pokemon_japan", "Pokémon Japanese"]];

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [gameOptions, setGameOptions] = useState(DEFAULT_GAME_OPTIONS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/subscription");
      setSub(data);
    } catch {
      setSub(null);
    }
  }, []);

  const refreshGames = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/games");
      const byId = Object.fromEntries((data.available || []).map((g) => [g.id, g.label]));
      const enabled = (data.enabled || []).filter((id) => byId[id]);
      const opts = [["", "All"], ...enabled.map((id) => [id, byId[id]])];
      // Japanese Pokémon is bundled with Pokémon — always offer it as a filter when Pokémon is on.
      if (enabled.includes("pokemon") && !enabled.includes("pokemon_japan")) {
        const i = opts.findIndex(([id]) => id === "pokemon");
        opts.splice(i + 1, 0, ["pokemon_japan", byId["pokemon_japan"] || "Pokémon Japanese"]);
      }
      setGameOptions(opts);
    } catch { /* keep defaults */ }
  }, []);

  useEffect(() => {
    if (user) { refresh(); refreshGames(); }
    else { setSub(null); setGameOptions(DEFAULT_GAME_OPTIONS); }
  }, [user, refresh, refreshGames]);

  return (
    <SubscriptionContext.Provider value={{ sub, refresh, gameOptions, refreshGames }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscription = () =>
  useContext(SubscriptionContext) || { sub: null, refresh: () => {}, gameOptions: DEFAULT_GAME_OPTIONS, refreshGames: () => {} };
